import { msalInstance, acquireBearerToken } from "./auth";

const apiVersion = import.meta.env.VITE_API_VERSION || "v3";

export async function sessionTracking(): Promise<void> {

    const bearerToken = await acquireBearerToken(msalInstance);
    if (!bearerToken) {
        console.warn("Skipping session tracking: authentication token unavailable");
        return;
    }

    try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
        await fetch(`${baseUrl}/api/${apiVersion}/session_tracking`, {
            method: "POST",
            headers: {
                Authorization: bearerToken
            }
        });
    } catch (error) {
        console.error("Error in sessionTracking API call:", error);
    }
}

// Enhanced session tracking that can be called multiple times for token refresh scenarios
export async function enhancedSessionTracking(): Promise<void> {

    
    console.log("enhancedSessionTracking called from:", new Error().stack?.split('\n')[2]?.trim());
    
    try {
        const bearerToken = await acquireBearerToken(msalInstance);
        if (!bearerToken) {
            console.warn("Skipping enhanced session tracking: authentication token unavailable");
            return;
        }

        const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
        
        const response = await fetch(`${baseUrl}/api/${apiVersion}/session_tracking`, {
            method: "POST",
            headers: {
                Authorization: bearerToken
            }
        });
        
        if (response.ok) {
            // Update the last session tracking time
            const data = await response.json();
            window.lastSessionTrackingTime = Date.now();
            window.currentSessionId = data.session_id;
            console.log("Session tracking successful at:", new Date().toISOString());
        } else {
            console.error("Session tracking failed with status:", response.status);
        }
    } catch (error) {
        console.error("Error in enhancedSessionTracking API call:", error);
    }
}

// Smart session tracking that only creates new sessions when access tokens are refreshed
export async function smartSessionTracking(): Promise<void> {

    // Guard to prevent recursive calls
    if (window.isSmartTrackingInProgress) {
        console.log("Smart session tracking already in progress, skipping to prevent infinite loop");
        return;
    }
    
    // Prevent running if initial session tracking is still in progress
    if (window.sessionTrackingCalled === false || window.sessionTrackingCalled === undefined) {
        console.log("Initial session tracking not completed yet, skipping smart tracking");
        return;
    }
    
    // Prevent running if initial session tracking hasn't set the timestamp yet
    if (!window.lastSessionTrackingTime) {
        console.log("Initial session tracking timestamp not set yet, skipping smart tracking");
        return;
    }
    
    console.log("smartSessionTracking called - checking timing...");
    console.log("window.lastSessionTrackingTime:", window.lastSessionTrackingTime);
    
    // Check if we recently tracked a session (within last 1 hour)
    if (window.lastSessionTrackingTime) {
        const timeSinceLastTrack = Date.now() - window.lastSessionTrackingTime;
        const ONE_HOUR = 60 * 60 * 1000;
        console.log("Time since last track:", Math.round(timeSinceLastTrack / (60 * 1000)), "minutes");
        if (timeSinceLastTrack < ONE_HOUR) {
            console.log("Session tracked recently (", Math.round(timeSinceLastTrack / (60 * 1000)), "minutes ago), skipping");
            return;
        }
    } else {
        console.log("No lastSessionTrackingTime set yet");
    }
    
    try {
        window.isSmartTrackingInProgress = true;
        
        // Simple approach: if we get here, it's been >1 hour, so track the session
        console.log("Session tracking after 1+ hour gap, creating new session record");
        await enhancedSessionTracking();
        
    } catch (error) {
        console.error("Error in smart session tracking:", error);
    } finally {
        // Always reset the guard
        window.isSmartTrackingInProgress = false;
    }
}
